import { useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { App } from "@capacitor/app";
import { isNative } from "@/lib/capacitor";
import { toast } from "sonner";

/** Root-level paths for bottom tabs — pressing back on these should exit or show toast. */
const TAB_ROOT_PATHS = ["/dashboard", "/pending-jobs", "/schedule", "/invoicing", "/more"];

/**
 * Hook that handles Android hardware back button behavior:
 * - Within a tab stack: navigates back
 * - At tab root: shows "press back again to exit" toast
 * - Double-back at root: exits app
 */
export function useAndroidBackButton() {
  const location = useLocation();
  const navigate = useNavigate();
  const exitPressedRef = useRef(false);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isTabRoot = useCallback(() => {
    return TAB_ROOT_PATHS.includes(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (!isNative) return;

    let listenerHandle: { remove: () => void } | null = null;

    App.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack && !isTabRoot()) {
        // Navigate back within the webview history
        window.history.back();
      } else if (exitPressedRef.current) {
        // Second press — exit the app
        App.exitApp();
      } else {
        // First press at root — show toast
        exitPressedRef.current = true;
        toast("Press back again to exit", { duration: 2000 });

        if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
        exitTimerRef.current = setTimeout(() => {
          exitPressedRef.current = false;
        }, 2000);
      }
    }).then((handle) => {
      listenerHandle = handle;
    });

    return () => {
      listenerHandle?.remove();
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
  }, [isTabRoot]);
}
